///
/// EarthBrowser - Permissions API
///
/// Created: 2025-11-15
/// Copyright (c) 2025 Geodata Labs
///

import { authenticatedApi } from "./API/authenticatedApi";

export interface PermissionResponse {
	permissions: Array<{
		permission_name: string;
		resource: string;
		action: string;
	}>;
	roles: Array<{
		id: number;
		name: string;
		priority: number;
		granted_at: string;
		expires_at: string | null;
	}>;
}

export interface CheckPermissionsRequest {
	permissions: string[];
}

export interface CheckPermissionsResponse {
	results: Record<string, boolean>;
}

export interface User {
	id: number;
	email: string;
	first_name: string;
	last_name: string;
	status: string;
	email_verified: boolean;
	created_at: string;
	last_login_at: string | null;
	roles: string[];
	device_count: number;
	active_sessions: number;
}

export interface UsersResponse {
	users: User[];
	pagination: {
		page: number;
		limit: number;
		total: number;
		pages: number;
	};
}

export interface SuspendUserRequest {
	reason: string;
	duration_days?: number;
	suspended_until?: string;
}

export interface GrantRoleRequest {
	user_id: number;
	expires_at?: string;
}

export interface RevokeRoleRequest {
	user_id: number;
}

export interface Role {
	id: number;
	name: string;
	description: string;
	priority: number;
	is_system: boolean;
	created_at: string;
	updated_at: string;
	permission_count?: number;
}

export interface Permission {
	id: number;
	name: string;
	resource: string;
	action: string;
	description: string;
	created_at: string;
}

export interface RoleWithPermissions extends Role {
	permissions: Permission[];
}

export interface RolesResponse {
	roles: Role[];
	total: number;
}

export interface PermissionsResponse {
	permissions: Permission[];
	grouped: Record<string, Permission[]>;
	total: number;
}

export interface AuditLogEntry {
	id: number;
	created_at: string;
	action: string;
	resource_type: string;
	resource_id: number;
	affected_user_email: string;
	actor_email: string;
	ip_address: string;
	metadata: Record<string, unknown> | null;
	old_values: Record<string, unknown> | null;
	new_values: Record<string, unknown> | null;
}

export interface AuditLogResponse {
	logs: AuditLogEntry[];
	pagination: {
		page: number;
		limit: number;
		total: number;
		pages: number;
	};
}

export interface AuditLogFilters {
	page?: number;
	limit?: number;
	user_id?: number;
	action?: string;
	start_date?: string;
	end_date?: string;
}

export const permissionsApi = authenticatedApi.injectEndpoints({
	endpoints: (builder) => ({
		getUserPermissions: builder.query<PermissionResponse, void>({
			query: () => "/api/user/permissions",
			providesTags: ["UserProfile"],
		}),

		checkPermissions: builder.mutation<CheckPermissionsResponse, CheckPermissionsRequest>({
			query: (body) => ({
				url: "/api/user/check-permissions",
				method: "POST",
				body,
			}),
		}),

		getAdminUsers: builder.query<UsersResponse, { page?: number; limit?: number; search?: string; status?: string; role?: string }>({
			query: ({ page = 1, limit = 20, search, status, role }) => {
				const params = new URLSearchParams({
					page: String(page),
					limit: String(limit),
				});
				if (search) params.append("search", search);
				if (status && status !== "all") params.append("status", status);
				if (role && role !== "all") params.append("role", role);
				return `/api/admin/users?${params}`;
			},
			providesTags: ["UserAccounts"],
		}),

		suspendUser: builder.mutation<{ success: boolean }, { userId: number; body: SuspendUserRequest }>({
			query: ({ userId, body }) => ({
				url: `/api/admin/users/${userId}/suspend`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["UserAccounts"],
		}),

		unsuspendUser: builder.mutation<{ success: boolean }, number>({
			query: (userId) => ({
				url: `/api/admin/users/${userId}/unsuspend`,
				method: "POST",
			}),
			invalidatesTags: ["UserAccounts"],
		}),

		grantRole: builder.mutation<{ success: boolean }, { roleId: number; body: GrantRoleRequest }>({
			query: ({ roleId, body }) => ({
				url: `/api/admin/roles/${roleId}/grant`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["UserAccounts", "UserProfile"],
		}),

		revokeRole: builder.mutation<{ success: boolean }, { roleId: number; body: RevokeRoleRequest }>({
			query: ({ roleId, body }) => ({
				url: `/api/admin/roles/${roleId}/revoke`,
				method: "DELETE",
				body,
			}),
			invalidatesTags: ["UserAccounts", "UserProfile"],
		}),

		getRoles: builder.query<RolesResponse, void>({
			query: () => "/api/admin/roles",
			providesTags: ["UserAccounts"],
		}),

		getRoleById: builder.query<RoleWithPermissions, number>({
			query: (roleId) => `/api/admin/roles/${roleId}`,
			providesTags: ["UserAccounts"],
		}),

		getPermissions: builder.query<PermissionsResponse, void>({
			query: () => "/api/admin/permissions",
		}),

		getAuditLog: builder.query<AuditLogResponse, AuditLogFilters>({
			query: (filters) => {
				const params = new URLSearchParams();
				if (filters.page) params.append("page", String(filters.page));
				if (filters.limit) params.append("limit", String(filters.limit));
				if (filters.user_id) params.append("user_id", String(filters.user_id));
				if (filters.action) params.append("action", filters.action);
				if (filters.start_date) params.append("start_date", filters.start_date);
				if (filters.end_date) params.append("end_date", filters.end_date);
				return `/api/admin/audit-log?${params}`;
			},
			providesTags: ["UserAccounts"],
		}),
	}),
});

export const {
	useGetUserPermissionsQuery,
	useCheckPermissionsMutation,
	useGetAdminUsersQuery,
	useSuspendUserMutation,
	useUnsuspendUserMutation,
	useGrantRoleMutation,
	useRevokeRoleMutation,
	useGetRolesQuery,
	useGetRoleByIdQuery,
	useGetPermissionsQuery,
	useGetAuditLogQuery,
} = permissionsApi;
